import { useCallback, useMemo, useState } from 'react';
import type { FundAccountPair } from '../types/dealerFundStatementPeriod.types';
import {
    buildFundAccountRows,
    filterFundAccounts,
    FUND_ACCOUNTS_PER_PAGE,
    pageOf,
    type FundAccountRow,
} from '../utils/fundAccountList';

export interface FundAccountListView {
    total: number;
    rows: FundAccountRow[];
    allRows: FundAccountRow[];
    matchCount: number;
    searchTerm: string;
    page: number;
    lastPage: number;
    from: number;
    to: number;
    perPage: number;
    onSearch: (term: string) => void;
    onPageChange: (page: number) => void;
}

export function useFundAccountList(pairs: FundAccountPair[], productNames: Map<string, string>): FundAccountListView {
    const [searchTerm, setSearchTerm] = useState('');
    const [requestedPage, setRequestedPage] = useState(1);
    const allRows = useMemo(() => buildFundAccountRows(pairs, productNames), [pairs, productNames]);
    const matches = useMemo(() => filterFundAccounts(allRows, searchTerm), [allRows, searchTerm]);
    const visible = pageOf(matches, requestedPage, FUND_ACCOUNTS_PER_PAGE);

    const onSearch = useCallback((term: string) => {
        setSearchTerm(term);
        setRequestedPage(1);
    }, []);

    return {
        total: allRows.length,
        rows: visible.rows,
        allRows,
        matchCount: matches.length,
        searchTerm,
        page: visible.page,
        lastPage: visible.lastPage,
        from: visible.from,
        to: visible.to,
        perPage: FUND_ACCOUNTS_PER_PAGE,
        onSearch,
        onPageChange: setRequestedPage,
    };
}
